import {
  AccountType as ApiAccountType,
  QboSourceType,
  SourceType,
  XeroSourceType,
  ZohoSourceType,
} from 'store/api/enums';
import {
  ActivityRoles,
  AccountType as AppAccountType,
  DataLogStatus,
  FilterAccounTypes,
} from 'store/app/enums';
import {
  ADD_NOTE_REQUEST,
  ADD_NOTE_SUCCESS,
  AWAITING_APPROVAL_REQUEST,
  AWAITING_APPROVAL_SUCCESS,
  CANCEL_RETURN_REQUEST,
  CANCEL_RETURN_SUCCESS,
  CANCEL_RETURN_FAILURE,
  CHANGE_ACTIVITY_ROLE,
  CHANGE_STATUS_AFTER_UPDATE,
  CHECK_ENTITY_STATUS_REQUEST,
  CHECK_ENTITY_STATUS_SUCCESS,
  CLEAR_RECONCILIATION_RUN,
  CLIENT_APPROVE_REQUEST,
  CLIENT_APPROVE_SUCCESS,
  EXCEPTIONS_ACTION_REQUEST,
  EXCEPTIONS_ACTION_SUCCESS,
  EXPORT_TO_CSV_DONE,
  EXPORT_TO_CSV_REQUEST,
  EXPORT_TO_EXCEL_DONE,
  EXPORT_TO_EXCEL_REQUEST,
  FETCH_ACTIVITY_LOGS_FAILURE,
  FETCH_ACTIVITY_LOGS_REQUEST,
  FETCH_ACTIVITY_LOGS_SUCCESS,
  FETCH_EXCEL_FILE_REQUEST,
  FETCH_EXCEL_FILE_SUCCESS,
  FETCH_RUN_DATA_FAILURE,
  FETCH_RUN_DATA_REQUEST,
  FETCH_RUN_DATA_SUCCESS,
  FETCH_TRANSACTION_ATTACHMENT_FAILURE,
  FETCH_TRANSACTION_ATTACHMENT_REQUEST,
  FETCH_TRANSACTION_ATTACHMENT_SUCCESS,
  FETCH_METADATA_ATTACHMENTS_FAILURE,
  FETCH_METADATA_ATTACHMENTS_REQUEST,
  FETCH_METADATA_ATTACHMENTS_SUCCESS,
  CLEAR_METADATA_ATTACHMENTS,
  FETCH_VAT_LOGS_REQUEST,
  FETCH_VAT_LOGS_SUCCESS,
  UPDATE_VAT_RETURN_LOG_STATUS,
  FETCH_VIEW_RUN_DATA_REQUEST,
  GENERATE_REPORT_FAILURE,
  GENERATE_REPORT_REQUEST,
  GENERATE_REPORT_SUCCESS,
  INTERNALLY_APPROVE_REQUEST,
  INTERNALLY_APPROVE_SUCCESS,
  LOAD_RECON_FAILURE,
  LOAD_RECON_REQUEST,
  RECON_GENERATE_LOAD_INVALIDATED,
  LOAD_RECON_SUCCESS,
  LOAD_REPORT_REQUEST,
  LOAD_REPORT_SUCCESS,
  LOAD_REPORT_TOTALS_REQUEST,
  LOCK_RETURN_REQUEST,
  LOCK_RETURN_SUCCESS,
  MANUAL_JOURNAL_POST_FAILURE,
  MANUAL_JOURNAL_POST_REQUEST,
  MANUAL_JOURNAL_POST_SUCCESS,
  NOT_APPROVE_REQUEST,
  NOT_APPROVE_SUCCESS,
  RESET_VAT_DATA,
  SAVE_RETURN_REQUEST,
  SAVE_RETURN_SUCCESS,
  UPDATE_REPORT_FIELDS_REQUEST,
  UPDATE_REPORT_FIELDS_SUCCESS,
  SEND_CLIENT_APPROVAL_REQUEST,
  SEND_CLIENT_APPROVAL_SUCCESS,
  SET_CURRENT_JOURNALS_PAGE,
  SET_TRANSACTION_FILTERS,
  SET_UPDATE_FROM_PAGE,
  SUBMIT_RETURN_REQUEST,
  SUBMIT_HMRC_RETURN_REQUEST,
  SUBMIT_RETURN_SUCCESS,
  SUBMIT_HMRC_RETURN_SUCCESS,
  UPDATE_ACTIVITY_ROLE_REQUEST,
  UPDATE_ACTIVITY_ROLE_SUCCESS,
  UPDATE_SUMMARIZED_DATA_SIZE,
  UPDATE_REPORT_FIELDS_FAILURE,
  UPLOAD_TRANSACTION_ATTACHMENT_REQUEST,
  SUBMIT_HMRC_RETURN_FAILURE,
} from './constants';
import { VatReturnLog, VatRunData } from './models';
import { User } from 'store/auth/models';
import { ExceptionsActionType } from './models/exceptionWrapper';
import {
  DataJournal,
  DataJournalType,
  JournalType,
  TaxReportSubsectionDataResponse,
  VatReportData,
} from './models/vatReportData';
import {
  ChangeActivityRoleAction,
  ChangeStatusAfterUpdateAction,
  FetchActivityLogsSuccessAction,
  FetchExcelFileSuccessAction,
  FetchRunDataSuccessAction,
  FetchTransactionAttachmentSuccessAction,
  FetchMetadataAttachmentsRequestAction,
  FetchMetadataAttachmentsSuccessAction,
  FetchVatLogsSuccessAction,
  GenerateReportFailureAction,
  GenerateReportSuccessAction,
  LoadReconRequestAction,
  LoadReconSuccessAction,
  SaveReturnSuccessAction,
  SetCurrentPageAction,
  SetTransactionFiltersAction,
  SetUpdateFromPageAction,
  UpdateSummarizedDataSizeAction,
  UpdateVatReturnLogStatusAction,
  VatAction,
  VatState,
} from './types';

const initialState: VatState = {
  prepLoading: false,
  loading: false,
  vatReturnLogs: [],
  vatRunData: undefined,
  vatReportData: undefined,
  reconciliation: undefined,
  reconciliationRun: null,
  activityLogs: undefined,
  success: false,
  updateFromPage: false,
  noDataFound: false,
  reportFilters: undefined,
  attachmentLoading: false,
  lastFetchedTransactionAttachments: {
    transactionId: null,
    attachments: [],
  },
  metadataAttachmentLoading: false,
  lastFetchedMetadataAttachments: {
    metadataId: null,
    fileName: null,
    attachments: [],
  },
  loadingState: {
    excelLoading: false,
    csvLoading: false,
  },
  backendError: false,
  reconciliationGenerateLoadEpoch: 0,
};

export const vatReducer = (
  state = initialState,
  action: VatAction
): VatState => {
  switch (action.type) {
    case SAVE_RETURN_REQUEST:
    case UPDATE_REPORT_FIELDS_REQUEST:
    case CANCEL_RETURN_REQUEST:
    case UPDATE_ACTIVITY_ROLE_REQUEST:
    case ADD_NOTE_REQUEST:
    case AWAITING_APPROVAL_REQUEST:
    case INTERNALLY_APPROVE_REQUEST:
    case SEND_CLIENT_APPROVAL_REQUEST:
    case CLIENT_APPROVE_REQUEST:
    case NOT_APPROVE_REQUEST:
    case SUBMIT_RETURN_REQUEST:
    case SUBMIT_HMRC_RETURN_REQUEST:
    case LOCK_RETURN_REQUEST:
    case CHECK_ENTITY_STATUS_REQUEST:
    case FETCH_EXCEL_FILE_REQUEST:
    case EXCEPTIONS_ACTION_REQUEST:
    case MANUAL_JOURNAL_POST_REQUEST:
      return handleRequest(state);
    case FETCH_VAT_LOGS_REQUEST:
      return handleFetchVatLogsRequest(state);
    case FETCH_RUN_DATA_REQUEST:
    case FETCH_VIEW_RUN_DATA_REQUEST:
      return handleFetchRunDataRequest(state);
    case GENERATE_REPORT_REQUEST:
    case LOAD_REPORT_REQUEST:
    case LOAD_REPORT_TOTALS_REQUEST:
      return handleGenerateReportRequest(state);
    case LOAD_RECON_REQUEST:
      return handleLoadReconRequest(state, action);
    case RECON_GENERATE_LOAD_INVALIDATED:
      return {
        ...state,
        reconciliationGenerateLoadEpoch:
          state.reconciliationGenerateLoadEpoch + 1,
        reconciliation: undefined,
        reconciliationRun: null,
        loading: false,
      };
    case FETCH_ACTIVITY_LOGS_REQUEST:
      return handleFetchActivityLogsRequest(state);
    case CANCEL_RETURN_SUCCESS:
    case UPDATE_ACTIVITY_ROLE_SUCCESS:
    case ADD_NOTE_SUCCESS:
    case AWAITING_APPROVAL_SUCCESS:
    case INTERNALLY_APPROVE_SUCCESS:
    case SEND_CLIENT_APPROVAL_SUCCESS:
    case CLIENT_APPROVE_SUCCESS:
    case NOT_APPROVE_SUCCESS:
    case SUBMIT_RETURN_SUCCESS:
    case SUBMIT_HMRC_RETURN_SUCCESS:
    case LOCK_RETURN_SUCCESS:
    case CHECK_ENTITY_STATUS_SUCCESS:
    case EXCEPTIONS_ACTION_SUCCESS:
    case MANUAL_JOURNAL_POST_SUCCESS:
    case UPDATE_REPORT_FIELDS_SUCCESS:
      return handleSuccess(state);
    case SAVE_RETURN_SUCCESS:
      return handleSaveReturnSuccess(state, action);
    case FETCH_RUN_DATA_SUCCESS:
      return handleFetchRunDataSuccess(state, action);
    case FETCH_VAT_LOGS_SUCCESS:
      return handleFetchVatLogsSuccess(state, action);
    case UPDATE_VAT_RETURN_LOG_STATUS:
      return handleUpdateVatReturnLogStatus(state, action);
    case GENERATE_REPORT_SUCCESS:
      return handleGenerateReportSuccess(state, action);
    case LOAD_RECON_SUCCESS:
      return handleLoadReconSuccess(state, action);
    case FETCH_ACTIVITY_LOGS_SUCCESS:
      return handleFetchActivityLogsSuccess(state, action);
    case FETCH_EXCEL_FILE_SUCCESS:
      return handleFetchExcelFileSuccess(state, action);
    case FETCH_RUN_DATA_FAILURE:
      return handleFetchRunDataFailure(state);
    case GENERATE_REPORT_FAILURE:
      return handleGenerateReportFailure(state, action);
    case LOAD_RECON_FAILURE:
      return handleLoadReconFailure(state);
    case CLEAR_RECONCILIATION_RUN:
      return { ...state, reconciliationRun: null };
    case FETCH_ACTIVITY_LOGS_FAILURE:
      return handleFetchActivityLogsFailure(state);
    case FETCH_TRANSACTION_ATTACHMENT_SUCCESS:
      return handleFetchTransactionAttachmentSuccess(state, action);
    case FETCH_METADATA_ATTACHMENTS_REQUEST:
      return handleFetchMetadataAttachmentsRequest(state, action);
    case FETCH_METADATA_ATTACHMENTS_SUCCESS:
      return handleFetchMetadataAttachmentsSuccess(state, action);
    case FETCH_METADATA_ATTACHMENTS_FAILURE:
      return handleFetchMetadataAttachmentsFailure(state);
    case CLEAR_METADATA_ATTACHMENTS:
      return handleClearMetadataAttachments(state);
    case FETCH_TRANSACTION_ATTACHMENT_FAILURE:
    case UPDATE_REPORT_FIELDS_FAILURE:
    case MANUAL_JOURNAL_POST_FAILURE:
    case SUBMIT_HMRC_RETURN_FAILURE:
    case CANCEL_RETURN_FAILURE:
      return handleFailure(state);
    case CHANGE_STATUS_AFTER_UPDATE:
      return handleChangeStatusAfterUpdate(state, action);
    case CHANGE_ACTIVITY_ROLE:
      return handleChangeActivityRole(state, action);
    case RESET_VAT_DATA:
      return handleResetVatData(state);
    case SET_UPDATE_FROM_PAGE:
      return handleSetUpdateFromPage(state, action);
    case SET_CURRENT_JOURNALS_PAGE:
      return handleSetCurrentJournalsPage(state, action);
    case SET_TRANSACTION_FILTERS:
      return handleSetTransactionFilters(state, action);
    case FETCH_TRANSACTION_ATTACHMENT_REQUEST:
    case UPLOAD_TRANSACTION_ATTACHMENT_REQUEST:
      return handleTransactionAttachmentRequest(state);
    case EXPORT_TO_EXCEL_REQUEST:
      return handleExportToExcelRequest(state);
    case EXPORT_TO_CSV_REQUEST:
      return handleExportToCSVRequest(state);
    case EXPORT_TO_EXCEL_DONE:
      return handleExportToExcelDone(state);
    case EXPORT_TO_CSV_DONE:
      return handleExportToCSVDone(state);
    case LOAD_REPORT_SUCCESS:
      return handleLoadReportSuccess(state);
    case UPDATE_SUMMARIZED_DATA_SIZE:
      return handleUpdateSummarizedDataSize(state, action);
    default:
      return state;
  }
};

function handleFetchRunDataRequest(state: VatState): VatState {
  return {
    ...state,
    prepLoading: true,
    vatRunData: state.updateFromPage ? state.vatRunData : undefined,
  };
}

function handleFetchRunDataSuccess(
  state: VatState,
  action: FetchRunDataSuccessAction
): VatState {
  return {
    ...state,
    prepLoading: false,
    vatRunData: action.payload.vatRunData,
  };
}

function handleFetchVatLogsRequest(state: VatState): VatState {
  // Logs are a background fetch — do not toggle `loading`, which drives the report
  // progress bar, tab spinners, and header action buttons.
  return state;
}

function handleFetchVatLogsSuccess(
  state: VatState,
  action: FetchVatLogsSuccessAction
): VatState {
  const newLogs = action.payload.vatReturnLogs || [];
  const affectedMappingIds = new Set(
    newLogs.map((log) => log.mappingId).filter(Boolean) as string[]
  );

  const keptLogs =
    affectedMappingIds.size === 0
      ? state.vatReturnLogs
      : state.vatReturnLogs.filter(
          (log) => !log.mappingId || !affectedMappingIds.has(log.mappingId)
        );

  return {
    ...state,
    vatReturnLogs: [...keptLogs, ...newLogs],
  };
}

function toVatReturnLogStatusKey(value: unknown): string {
  const str = String(value ?? '').trim();
  if (!str) return '';
  const maybeEnumKey = str as keyof typeof DataLogStatus;
  if (DataLogStatus[maybeEnumKey] !== undefined) {
    return maybeEnumKey;
  }
  const matched = (
    Object.entries(DataLogStatus) as [keyof typeof DataLogStatus, string][]
  ).find(([, enumValue]) => enumValue === str);
  return matched ? String(matched[0]) : str;
}

function handleUpdateVatReturnLogStatus(
  state: VatState,
  action: UpdateVatReturnLogStatusAction
): VatState {
  const { dataLogId, mappingId, status } = action.payload;
  const statusKey = toVatReturnLogStatusKey(status);
  if (!statusKey) {
    return state;
  }

  const normalizedDataLogId = String(dataLogId ?? '')
    .trim()
    .toLowerCase();
  let updated = false;

  const vatReturnLogs = state.vatReturnLogs.map((log) => {
    if (
      log.mappingId === mappingId &&
      String(log.uuid ?? '')
        .trim()
        .toLowerCase() === normalizedDataLogId
    ) {
      updated = true;
      return { ...log, status: statusKey };
    }
    return log;
  });

  return updated ? { ...state, vatReturnLogs } : state;
}

function handleFetchRunDataFailure(state: VatState): VatState {
  return {
    ...state,
    prepLoading: false,
    vatRunData: undefined,
  };
}

function handleGenerateReportRequest(state: VatState): VatState {
  return {
    ...state,
    loading: true,
    vatReportData: undefined,
    success: false,
    noDataFound: false,
    updateFromPage: false,
    backendError: false,
  };
}

// Floor SYSTEM_GENERATED boolean boxes (e.g. Malta 42/43) whose *summed* value is
// negative down to 0. The backend returns the raw signed value per page so pages can
// be netted against each other; the "< 0 -> 0" floor must be applied once, after all
// pages are summed. For the paged generation flow this completion point is the
// terminal empty-page early return below (lastPage/isEmpty never reaches the merge
// body), so the floor has to be applied there.
const floorNegativeBooleanBoxes = (
  subs: TaxReportSubsectionDataResponse[] | undefined
): TaxReportSubsectionDataResponse[] =>
  (subs ?? []).map((fs) =>
    fs.booleanExpression && fs.net < 0 ? { ...fs, net: 0, tax: 0 } : fs
  );

function handleGenerateReportSuccess(
  state: VatState,
  action: GenerateReportSuccessAction
): VatState {
  // If the backend indicates this page is empty, don't overwrite
  // the existing vatReportData – just mark the load as completed.
  // However, normalise pagination so progress logic can treat it as "done".
  if (action.payload.vatReportData?.empty) {
    // If we already have report data, keep it but force totalPages/page to 0.
    if (state.vatReportData) {
      const incomingSummarizedSize =
        action.payload.vatReportData?.summarizedSize;
      return {
        ...state,
        loading: false,
        success: true,
        noDataFound: false,
        backendError: false,
        vatReportData: {
          ...state.vatReportData,
          subsections: floorNegativeBooleanBoxes(state.vatReportData.subsections),
          totalPages: 0,
          page: 0,
          // Terminal empty page (or load) may carry the final summarized count.
          summarizedSize:
            typeof incomingSummarizedSize === 'number'
              ? incomingSummarizedSize
              : state.vatReportData.summarizedSize,
        },
      };
    }

    const incoming = action.payload.vatReportData;
    const IGNORE_EMPTY_SENTINEL_KEYS = new Set([
      'empty',
      'recap',
      'accrualsLastId',
      'cashAccountingLastId',
      'status',
    ]);
    const hasReportContent = Object.keys(incoming || {}).some((key) => {
      if (IGNORE_EMPTY_SENTINEL_KEYS.has(key)) {
        return false;
      }
      const value = incoming[key];
      if (value == null) {
        return false;
      }
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      if (typeof value === 'object') {
        return Object.keys(value).length > 0;
      }
      return true;
    });

    // No-data runs can return the full return layout in a single empty-marked
    // payload; keep it so boxes/totals are available, not only the progress bar.
    if (hasReportContent) {
      return {
        ...state,
        loading: false,
        success: true,
        noDataFound: false,
        backendError: false,
        vatReportData: {
          ...incoming,
          subsections: floorNegativeBooleanBoxes(incoming.subsections),
          totalPages: 0,
          page: 0,
          journals: incoming.journals ?? [],
          exceptions: incoming.exceptions ?? [],
          summarizedJournals: incoming.summarizedJournals ?? [],
          transactionsSize: incoming.transactionsSize ?? 0,
          exceptionsSize: incoming.exceptionsSize ?? 0,
          summarizedSize:
            typeof incoming.summarizedSize === 'number'
              ? incoming.summarizedSize
              : 0,
        },
      };
    }

    // Sentinel-only empty page: nothing to merge, just mark complete.
    return {
      ...state,
      loading: false,
      success: true,
      noDataFound: false,
      backendError: false,
    };
  }

  // Prefer a positive size from this page; keep prior size otherwise so early
  // pages (or SSE-updated size) are not wiped by omitted/zero fields mid-stream.
  // On lastPage, an explicit 0 from the API is authoritative.
  const previousSummarizedSize = state?.vatReportData?.summarizedSize || 0;
  const incomingSummarizedSize = action.payload.vatReportData?.summarizedSize;
  let summarizedCount = previousSummarizedSize;
  if (typeof incomingSummarizedSize === 'number') {
    if (incomingSummarizedSize > 0) {
      summarizedCount = incomingSummarizedSize;
    } else if (action.payload.lastPage) {
      summarizedCount = incomingSummarizedSize;
    }
  }
  if (action.payload.firstPage) {
    state.vatReportData = undefined;
    if (!(typeof incomingSummarizedSize === 'number' && incomingSummarizedSize > 0)) {
      summarizedCount = typeof incomingSummarizedSize === 'number' ? incomingSummarizedSize : 0;
    }
  }

  const stateSections = state.vatReportData?.sections
    ? state.vatReportData?.sections
    : [];
  const stateSubsections: TaxReportSubsectionDataResponse[] = state
    .vatReportData?.subsections
    ? state.vatReportData?.subsections
    : [];
  const newSections = action.payload.vatReportData.sections;
  const newSubsections = action.payload.vatReportData.subsections;
  const sections =
    !newSections || newSections.length === 0 ? stateSections : newSections;
  let subsections: TaxReportSubsectionDataResponse[] = [];
  if (newSubsections && newSubsections.length > 0) {
    if (stateSubsections && stateSubsections.length > 0) {
      newSubsections.forEach((ns) => {
        const savedSub = stateSubsections.find((ss) => ss.code === ns.code);
        if (savedSub) {
          const net = savedSub.net + ns.net;
          const tax = savedSub.tax + ns.tax;

          const newSub = { ...ns, net, tax };
          subsections.push(newSub);
        }
      });
    } else {
      subsections = newSubsections;
    }
  } else {
    subsections = stateSubsections;
  }

  let adjustedSubsections: TaxReportSubsectionDataResponse[] =
    action.payload.vatReportData.adjustedSubsections || [];
  const finalSubsections = [...subsections];
  if (action.payload.lastPage) {
    finalSubsections.forEach((fs, idx) => {
      if (fs.booleanExpression && fs.net < 0) {
        subsections[idx].net = 0;
        subsections[idx].tax = 0;
      }
    });

    if (!adjustedSubsections || adjustedSubsections.length === 0) {
      adjustedSubsections = state.vatReportData?.adjustedSubsections || [];
    }
  }

  let journals: DataJournal[];

  journals =
    action.payload.vatReportData.journals?.filter(
      (j) =>
        j.type !== DataJournalType.EXCEPTION ||
        (j.type === DataJournalType.EXCEPTION &&
          (j.isProcessed ||
            (j.exceptionActionType !== undefined &&
              ExceptionsActionType[j.exceptionActionType] ===
                ExceptionsActionType.CURRENT_RETURN)))
    ) ?? [];

  journals.sort((a, b) => {
    if (a.type === b.type) {
      if (a.exceptionActionType && b.exceptionActionType) {
        return a.exceptionActionType > b.exceptionActionType
          ? 1
          : b.exceptionActionType > a.exceptionActionType
          ? -1
          : 0;
      }
      return (
        new Date(b.journalDate).valueOf() - new Date(a.journalDate).valueOf()
      );
    }
    return a.type > b.type ? 1 : b.type > a.type ? -1 : 0;
  });

  let exceptions: DataJournal[] = action.payload.vatReportData.exceptions ?? [];

  exceptions.map((vrd) => {
    vrd.accountType = AppAccountType[vrd.accountType];
    vrd.sourceType = SourceType[vrd.sourceType];
    if (!vrd.exceptionActionType) vrd.exceptionActionType = 'UNRESOLVED';
  });
  let summarizedJournals: DataJournal[] = [];

  let transactionsSize = state.vatReportData?.transactionsSize;
  if (action.payload.vatReportData.transactionsSize) {
    if (!transactionsSize) {
      transactionsSize = 0;
    }
    transactionsSize += action.payload.vatReportData.transactionsSize;
  }

  let exceptionsSize = state.vatReportData?.exceptionsSize;
  if (action.payload.vatReportData.exceptionsSize) {
    if (!exceptionsSize) {
      exceptionsSize = 0;
    }
    exceptionsSize += action.payload.vatReportData.exceptionsSize;
  }

  if (state.vatReportData?.journals && action.payload.vatReportData.journals) {
    const savedJournals = [...state.vatReportData.journals];
    const newJournals = [...action.payload.vatReportData.journals];
    journals = savedJournals.concat(newJournals);
  }

  if (
    state.vatReportData?.exceptions &&
    action.payload.vatReportData.exceptions
  ) {
    const savedExceptions = [...state.vatReportData.exceptions];
    const newExceptions = [...action.payload.vatReportData.exceptions];
    exceptions = savedExceptions.concat(newExceptions);
  }

  if (action.payload.lastPage) {
    const groupedTransactions: Map<string, DataJournal[]> = new Map<
      string,
      DataJournal[]
    >();
    journals.forEach((j) => {
      const key =
        j.journalDate +
        '-' +
        j.sourceId +
        '-' +
        j.sourceType +
        '-' +
        j.uploadDate;
      if (groupedTransactions.has(key)) {
        const savedJournals: any = groupedTransactions.get(key);
        const newValue: any = [...savedJournals, j];
        groupedTransactions.set(key, newValue);
      } else {
        const freshVal = [j];
        groupedTransactions.set(key, freshVal);
      }
      const accountType = AppAccountType[j.accountType];
      //const sourceType = SourceType[j.sourceType];
      // if (sourceType) {
      //   j.sourceType = sourceType;
      // }

      if (accountType) {
        j.accountType = accountType;
      }
    });

    groupedTransactions?.forEach((transactions) => {
      let totalAmount = 0;
      transactions.forEach((t) => {
        const addAmount = totalAmount + t.netAmount;
        totalAmount = parseFloat(addAmount.toFixed(2));
      });
      if (totalAmount !== 0) summarizedJournals.push(...transactions);
    });

    exceptions.sort(function (a, b) {
      return (
        new Date(b.journalDate).valueOf() - new Date(a.journalDate).valueOf()
      );
    });
  }

  // Preserve/accumulate reconciliation flags across paged or partial payloads.
  // Some responses can omit/reset these fields, which would otherwise hide the warning.
  const previousHasReconciliationExceptions =
    state.vatReportData?.hasReconciliationExceptions ?? false;
  const incomingHasReconciliationExceptions =
    action.payload.vatReportData?.hasReconciliationExceptions ?? false;
  const hasReconciliationExceptions =
    previousHasReconciliationExceptions || incomingHasReconciliationExceptions;

  const previousIncorrectTaxRates =
    state.vatReportData?.incorrectTaxRates || [];
  const incomingIncorrectTaxRates =
    action.payload.vatReportData?.incorrectTaxRates || [];
  const incorrectTaxRates = Array.from(
    new Set([...previousIncorrectTaxRates, ...incomingIncorrectTaxRates])
  );


  return {
    ...state,
    loading: !action.payload.lastPage,
    vatReportData: {
      ...action.payload.vatReportData,
      journals,
      exceptions,
      summarizedJournals,
      sections,
      subsections,
      adjustedSubsections,
      transactionsSize: transactionsSize || 0,
      exceptionsSize: exceptionsSize || 0,
      detailedSize: transactionsSize || 0,
      hasReconciliationExceptions,
      incorrectTaxRates,
      summarizedSize: summarizedCount,
    },
    success: action.payload.lastPage,
    noDataFound: false,
    backendError: false,
  };
}

function handleGenerateReportFailure(
  state: VatState,
  action: GenerateReportFailureAction
): VatState {
  return {
    ...state,
    loading: false,
    vatReportData: undefined,
    success: false,
    noDataFound: action.payload.noDataFound,
    backendError: true,
  };
}

function mergeActivityUsers(
  existing: User[] | undefined,
  incoming: User[]
): User[] {
  if (!incoming.length) {
    return existing ?? [];
  }
  const byKey = new Map<string, User>();
  [...(existing ?? []), ...incoming].forEach((user) => {
    const key = user.accountUuid || user.userUuid;
    if (key) {
      byKey.set(key, user);
    }
  });
  return Array.from(byKey.values());
}

function handleSaveReturnSuccess(
  state: VatState,
  action: SaveReturnSuccessAction
): VatState {
  const status = 'SAVED';
  const { dataLogId, users, currentUser, generatedDate, updateFromProvider } =
    action.payload;
  const currentStaff = state.vatRunData?.staff;
  const preparerId = currentUser.accountUuid;
  const staffFromMembers = (users ?? []).filter(
    (u) => ApiAccountType[u.accountType] !== ApiAccountType.CLIENT
  );
  const staff = mergeActivityUsers(currentStaff, staffFromMembers);
  const currentClients = state.vatRunData?.clients;
  const clientsFromMembers = (users ?? []).filter(
    (u) => ApiAccountType[u.accountType] === ApiAccountType.CLIENT
  );
  const clients = mergeActivityUsers(currentClients, clientsFromMembers);
  const vatRunData = {
    ...state.vatRunData,
    dataLogId,
    staff,
    clients,
    status,
    preparerId,
    generatedDate,
    updateFromProvider,
  } as VatRunData;

  return {
    ...state,
    loading: false,
    vatRunData,
  };
}

function handleLoadReconRequest(
  state: VatState,
  action: LoadReconRequestAction
): VatState {
  return {
    ...state,
    loading: true,
    reconciliation: undefined,
    reconciliationRun: {
      connectionId: action.payload.tokenId,
      asAtDate: action.payload.asAtDate,
      currency: action.payload.currency,
      mappingId: action.payload.mappingId,
    },
  };
}

function handleLoadReconSuccess(
  state: VatState,
  action: LoadReconSuccessAction
): VatState {
  return {
    ...state,
    loading: false,
    reconciliation: action.payload.reconMap,
  };
}

function handleLoadReconFailure(state: VatState): VatState {
  return {
    ...state,
    loading: false,
    reconciliation: undefined,
  };
}

function handleFetchActivityLogsRequest(state: VatState): VatState {
  return {
    ...state,
    loading: true,
    activityLogs: undefined,
  };
}

function handleFetchActivityLogsSuccess(
  state: VatState,
  action: FetchActivityLogsSuccessAction
): VatState {
  return {
    ...state,
    loading: false,
    activityLogs: action.payload.activityLogs,
  };
}

function handleFetchActivityLogsFailure(state: VatState): VatState {
  return {
    ...state,
    loading: false,
    activityLogs: undefined,
  };
}

function handleChangeStatusAfterUpdate(
  state: VatState,
  action: ChangeStatusAfterUpdateAction
): VatState {
  let status = '';
  switch (action.payload.status) {
    case DataLogStatus.SAVED:
      status = 'SAVED';
      break;
    case DataLogStatus.NOT_SAVED:
      status = 'NOT_SAVED';
      break;
    case DataLogStatus.AWAITING_APPROVAL:
      status = 'AWAITING_APPROVAL';
      break;
    case DataLogStatus.INTERNAL_APPROVED:
      status = 'INTERNAL_APPROVED';
      break;
    case DataLogStatus.AWAITING_CLIENT_APPROVAL:
      status = 'AWAITING_CLIENT_APPROVAL';
      break;
    case DataLogStatus.CLIENT_APPROVED:
      status = 'CLIENT_APPROVED';
      break;
    case DataLogStatus.SUBMITTED:
      status = 'SUBMITTED';
      break;
    case DataLogStatus.LOCKED:
      status = 'LOCKED';
      break;
    case DataLogStatus.EXCEPTIONS_MODIFIED:
      status = 'EXCEPTIONS_MODIFIED';
      break;
  }
  if (state.vatRunData) {
    const vatRunData: VatRunData = {
      ...state.vatRunData,
      status,
    };
    return {
      ...state,
      vatRunData,
    };
  }
  return state;
}

function handleChangeActivityRole(
  state: VatState,
  action: ChangeActivityRoleAction
): VatState {
  if (state.vatRunData) {
    let vatRunData: VatRunData;
    switch (action.payload.activityRole) {
      case ActivityRoles.PREPARER:
        const preparerRow = state.vatRunData.staff?.find(
          (vrd) => vrd.userUuid === action.payload.userId
        );
        const preparer = preparerRow
          ? preparerRow.firstName + ' ' + preparerRow.lastName
          : '';
        vatRunData = {
          ...state.vatRunData,
          preparerId: action.payload.userId,
          preparer,
        };
        break;
      case ActivityRoles.REVIEWER:
        const reviewerRow = state.vatRunData.staff?.find(
          (vrd) => vrd.userUuid === action.payload.userId
        );
        const reviewer = reviewerRow
          ? reviewerRow.firstName + ' ' + reviewerRow.lastName
          : '';
        vatRunData = {
          ...state.vatRunData,
          reviewerId: action.payload.userId,
          reviewer,
        };
        break;
      case ActivityRoles.CLIENT:
        const clientRow = state.vatRunData.clients?.find(
          (vrd) => vrd.userUuid === action.payload.userId
        );
        const client = clientRow
          ? clientRow.firstName + ' ' + clientRow.lastName
          : '';
        vatRunData = {
          ...state.vatRunData,
          clientId: action.payload.userId,
          client,
        };
        break;
      case ActivityRoles.REMOVE_REVIEWER:
        vatRunData = {
          ...state.vatRunData,
          reviewerId: undefined,
          reviewer: undefined,
        };
        break;
      case ActivityRoles.REMOVE_CLIENT:
        vatRunData = {
          ...state.vatRunData,
          clientId: '',
          client: '',
        };
        break;
    }
    return {
      ...state,
      vatRunData,
    };
  }
  return state;
}

function handleSetUpdateFromPage(
  state: VatState,
  action: SetUpdateFromPageAction
) {
  return {
    ...state,
    updateFromPage: action.payload.value,
  };
}
// Common
function handleRequest(state: VatState): VatState {
  return {
    ...state,
    loading: true,
  };
}

function handleSuccess(state: VatState): VatState {
  return {
    ...state,
    loading: false,
  };
}

function handleFailure(state: VatState): VatState {
  return {
    ...state,
    loading: false,
    attachmentLoading: false,
  };
}

// Reset report session state but keep entity submission history for list pages.
function handleResetVatData(state: VatState): VatState {
  return {
    ...initialState,
    vatReturnLogs: state.vatReturnLogs,
  };
}

// excel file

function handleFetchExcelFileSuccess(
  state: VatState,
  action: FetchExcelFileSuccessAction
): VatState {
  return {
    ...state,
    loading: false,
    excelFileData: action.payload.data,
  };
}

function handleSetCurrentJournalsPage(
  state: VatState,
  action: SetCurrentPageAction
): VatState {
  const { transactionType, journals, total } = action.payload;

  if (journals?.length > 0) {
    for (const journal of journals) {
      journal.accountType = FilterAccounTypes[journal.accountType];

      if (transactionType == JournalType.EXCEPTION) {
        if (!journal.exceptionActionType) {
          journal.exceptionActionType = 'UNRESOLVED';
        }
      }
    }
  }

  if (!state.vatReportData) {
    return state;
  }

  if (transactionType === JournalType.TRANSACTION) {
    return {
      ...state,
      vatReportData: {
        ...state.vatReportData,
        journals: journals,
        transactionsSize: total,
        detailedSize: state.vatReportData?.detailedSize ?? total,
      } as VatReportData,
    };
  }

  if (transactionType === JournalType.EXCEPTION) {
    return {
      ...state,
      vatReportData: {
        ...state.vatReportData,
        exceptions: journals,
        exceptionsSize: total,
        detailedSize: state.vatReportData?.detailedSize ?? total,
      } as VatReportData,
    };
  }

  return state;
}

function handleSetTransactionFilters(
  state: VatState,
  action: SetTransactionFiltersAction
): VatState {
  const { codes, accounts, taxRates, provider } = action.payload;

  const accountCode = new Set<string>();
  const accountType = new Set<string>();
  const accountName = new Set<string>();
  if (accounts && accounts?.length > 0) {
    for (const account of accounts) {
      if (account.code) {
        accountCode.add(account.code);
      }
      if (account.type) {
        accountType.add(account.type);
      }
      if (account.name) {
        accountName.add(account.name);
      }
    }
  }

  let sourceType: string[][] = [];
  if (provider == 'xero') {
    sourceType = [Object.values(XeroSourceType), Object.keys(XeroSourceType)];
  } else if (provider == 'quickbooks') {
    sourceType = [Object.keys(QboSourceType), Object.keys(QboSourceType)];
  } else if (provider == 'zoho') {
    sourceType = [Object.values(ZohoSourceType), Object.keys(ZohoSourceType)];
  }

  const taxRateNames = new Set<string>();
  if (taxRates && taxRates.length > 0) {
    for (const taxRate of taxRates) {
      if (taxRate.name) {
        taxRateNames.add(taxRate.name);
      }
    }
  }

  const typesMap: string[][] = [];
  if (accountType) {
    const left: string[] = [];
    const right: string[] = [];
    for (const type of accountType) {
      left.push(FilterAccounTypes[type]);
      right.push(type);
    }

    typesMap[0] = left;
    typesMap[1] = right;
  }

  let actionTypes: string[][] = [
    Object.values(ExceptionsActionType),
    Object.keys(ExceptionsActionType),
  ];
  actionTypes[0] = actionTypes[0].splice(0, actionTypes[0].length - 1);
  actionTypes[1] = actionTypes[1].splice(0, actionTypes[1].length - 1);

  return {
    ...state,
    reportFilters: {
      selectedValue: codes ? codes : [],
      sourceType: sourceType,
      accountCode: [...accountCode].sort(),
      accountName: [...accountName].sort(),
      accountType: typesMap,
      taxName: [...taxRateNames].sort(),
      exceptionActionType: actionTypes,
    },
  };
}

function handleFetchTransactionAttachmentSuccess(
  state: VatState,
  action: FetchTransactionAttachmentSuccessAction
): VatState {
  const lastFetched = {
    transactionId: action.payload.transactionId,
    attachments: action.payload.transactionAttachments,
  };

  if (!state.vatReportData?.journals) {
    return {
      ...state,
      attachmentLoading: false,
      lastFetchedTransactionAttachments: lastFetched,
    };
  }

  const updatedJournals = state.vatReportData.journals.map((journal) => {
    const transactionId = action.payload.transactionId;
    if (
      journal.sourceId === transactionId ||
      journal.invoiceId === transactionId
    ) {
      const existingAttachments = journal.transactionAttachments || [];
      const newAttachments = action.payload.transactionAttachments;

      // If source is 'fetch', replace all attachments
      // If source is 'upload', append new attachments to existing ones
      const finalAttachments =
        action.payload.source === 'fetch'
          ? newAttachments
          : [...newAttachments, ...existingAttachments];

      return {
        ...journal,
        transactionAttachments: finalAttachments,
        hasAttachments: finalAttachments.length > 0,
      };
    }
    return journal;
  });

  const updatedSummarizedJournals = state.vatReportData.summarizedJournals?.map(
    (journal) => {
      if (journal.sourceId === action.payload.transactionId) {
        const existingAttachments = journal.transactionAttachments || [];
        const newAttachments = action.payload.transactionAttachments;

        // If source is 'fetch', replace all attachments
        // If source is 'upload', append new attachments to existing ones
        const finalAttachments =
          action.payload.source === 'fetch'
            ? newAttachments
            : [...newAttachments, ...existingAttachments];

        return {
          ...journal,
          transactionAttachments: finalAttachments,
          hasAttachments: finalAttachments.length > 0,
        };
      }
      return journal;
    }
  );

  return {
    ...state,
    attachmentLoading: false,
    lastFetchedTransactionAttachments: lastFetched,
    vatReportData: {
      ...state.vatReportData,
      journals: updatedJournals,
      summarizedJournals:
        updatedSummarizedJournals || state.vatReportData.summarizedJournals,
    },
  };
}

function handleTransactionAttachmentRequest(state: VatState): VatState {
  return {
    ...state,
    attachmentLoading: true,
  };
}

function handleFetchMetadataAttachmentsRequest(
  state: VatState,
  action: FetchMetadataAttachmentsRequestAction
): VatState {
  return {
    ...state,
    metadataAttachmentLoading: true,
    lastFetchedMetadataAttachments: {
      metadataId: action.payload.metadataId,
      fileName: action.payload.fileName ?? null,
      attachments: [],
    },
  };
}

function handleFetchMetadataAttachmentsSuccess(
  state: VatState,
  action: FetchMetadataAttachmentsSuccessAction
): VatState {
  return {
    ...state,
    metadataAttachmentLoading: false,
    lastFetchedMetadataAttachments: {
      metadataId: action.payload.metadataId,
      fileName:
        action.payload.fileName ??
        state.lastFetchedMetadataAttachments.fileName,
      attachments: action.payload.attachments,
    },
  };
}

function handleFetchMetadataAttachmentsFailure(state: VatState): VatState {
  return {
    ...state,
    metadataAttachmentLoading: false,
  };
}

function handleClearMetadataAttachments(state: VatState): VatState {
  return {
    ...state,
    metadataAttachmentLoading: false,
    lastFetchedMetadataAttachments: {
      metadataId: null,
      fileName: null,
      attachments: [],
    },
  };
}

function handleExportToExcelRequest(state: VatState): VatState {
  return {
    ...state,
    loadingState: {
      ...state.loadingState,
      excelLoading: true,
    },
  };
}

function handleExportToCSVRequest(state: VatState): VatState {
  return {
    ...state,
    loadingState: {
      ...state.loadingState,
      csvLoading: true,
    },
  };
}

function handleExportToExcelDone(state: VatState): VatState {
  return {
    ...state,
    loadingState: {
      ...state.loadingState,
      excelLoading: false,
    },
  };
}

function handleExportToCSVDone(state: VatState): VatState {
  return {
    ...state,
    loadingState: {
      ...state.loadingState,
      csvLoading: false,
    },
  };
}

function handleLoadReportSuccess(state: VatState): VatState {
  return {
    ...state,
    loading: false,
    success: true,
  };
}

function handleUpdateSummarizedDataSize(
  state: VatState,
  action: UpdateSummarizedDataSizeAction
): VatState {
  if (!state.vatReportData) return state;

  return {
    ...state,
    vatReportData: {
      ...state.vatReportData,
      summarizedSize: action.payload.size,
    },
  };
}
